import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { UrlStateAdapter } from '../../components/product/filters/urlAdapter'

interface Pagination {
  page: number
  page_size: number
  total: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

interface CategoryPaginationProps {
  pagination: Pagination
  adapter: UrlStateAdapter
}

/**
 * Pagination bar for the SSR category page. Writes the `page` param
 * through the Vike URL adapter (not react-router) so filter state is
 * preserved and no full-page reload is triggered.
 */
export default function CategoryPagination({ pagination, adapter }: CategoryPaginationProps) {
  const { page, total_pages, has_prev, has_next } = pagination

  if (total_pages <= 1) return null

  function goTo(p: number) {
    const next = new URLSearchParams(adapter.searchParams)
    next.set('page', String(p))
    adapter.setSearchParams(next)
  }

  const pages: (number | '...')[] = []
  if (total_pages <= 7) {
    for (let i = 1; i <= total_pages; i++) pages.push(i)
  } else {
    pages.push(1)
    if (page > 3) pages.push('...')
    for (let i = Math.max(2, page - 1); i <= Math.min(total_pages - 1, page + 1); i++) {
      pages.push(i)
    }
    if (page < total_pages - 2) pages.push('...')
    pages.push(total_pages)
  }

  return (
    <nav className="flex items-center justify-center gap-1 mt-6" aria-label="Paginering">
      <button
        onClick={() => goTo(page - 1)}
        disabled={!has_prev}
        className={cn(
          'p-2 rounded-md text-sm transition-colors',
          has_prev
            ? 'hover:bg-neutral-100 text-neutral-700'
            : 'text-neutral-300 cursor-not-allowed',
        )}
        aria-label="Vorige pagina"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      {pages.map((p, i) =>
        p === '...' ? (
          <span key={`ellipsis-${i}`} className="px-2 text-neutral-600 text-sm">
            &hellip;
          </span>
        ) : (
          <button
            key={p}
            onClick={() => goTo(p as number)}
            className={cn(
              'min-w-[36px] h-9 px-2 rounded-md text-sm font-medium transition-colors',
              p === page
                ? 'bg-primary text-white'
                : 'hover:bg-neutral-100 text-neutral-700',
            )}
            aria-current={p === page ? 'page' : undefined}
          >
            {p}
          </button>
        ),
      )}

      <button
        onClick={() => goTo(page + 1)}
        disabled={!has_next}
        className={cn(
          'p-2 rounded-md text-sm transition-colors',
          has_next
            ? 'hover:bg-neutral-100 text-neutral-700'
            : 'text-neutral-300 cursor-not-allowed',
        )}
        aria-label="Volgende pagina"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </nav>
  )
}
